/// <amd-module name="TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Sources/AboutProjectSource" />
import { wait } from "VSS/Core/Util/Promise";
import { IVssPageContext } from "VSS/Platform/Context";
import { JsonPatchDocument, JsonPatchOperation, Operation } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";

import { TeamProject } from "Tfs/Platform/Contracts/Core";
import { ICoreRestClient } from "Tfs/Platform/RestClient/Core";

import { Constants } from "TFS/Views/ProjectOverviewPage/Constants";
import { OperationStatus } from "TFS/Views/ProjectOverviewPage/Contracts/Operations";
import { AboutProjectScenarios, ProjectScopeName, ProjectTagsConstants } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Constants";
import { AboutProjectData } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Models";
import { IOperationsRestClient } from "TFS/Views/ProjectOverviewPage/RestClient/Operations";

export interface AboutProjectDataProviderData {
    aboutProjectData: AboutProjectData;
}

export interface AllProjectTagsInCollectionDataProviderData {
    allProjectTagsInCollection: string[];
}

export class AboutProjectSource {
    private static PROVIDER_ID = "ms.vss-tfs-web.about-project-data-provider-verticals";
    private static ALL_PROJECT_TAGS_IN_COLLECTION_PROVIDER_ID = "ms.vss-tfs-web.collection-all-project-tags-data-provider-verticals";

    private _performanceService: IVssPerformanceService;

    constructor(private _pageContext: IVssPageContext) {
        this._performanceService = this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
    }

    public getAboutProjectDataAsync(): Promise<AboutProjectData | undefined> {
        this._performanceService.startScenario(AboutProjectScenarios.GetAboutProjectData, false);
        return this._pageContext
            .getService<IVssContributionService>("IVssContributionService")
            .getDataAsync<AboutProjectDataProviderData>(AboutProjectSource.PROVIDER_ID)
            .then(
                (data: AboutProjectDataProviderData | undefined) => {
                    this._performanceService.endScenario(Constants.Area, AboutProjectScenarios.GetAboutProjectData, false);
                    return data && data.aboutProjectData ? data.aboutProjectData : undefined;
                },
                (error: Error) => {
                    this._performanceService.endScenario(Constants.Area, AboutProjectScenarios.GetAboutProjectData, false, undefined, {
                        status: "failed"
                    });
                    throw error;
                }
            );
    }

    public getAllProjectTagsInCollectionDataAsync(): Promise<string[]> {
        this._performanceService.startScenario(AboutProjectScenarios.GetAllProjectTagsInCollection, false);
        return this._pageContext
            .getService<IVssContributionService>("IVssContributionService")
            .getDataAsync<AllProjectTagsInCollectionDataProviderData>(AboutProjectSource.ALL_PROJECT_TAGS_IN_COLLECTION_PROVIDER_ID)
            .then(
                (data: AllProjectTagsInCollectionDataProviderData | undefined) => {
                    this._performanceService.endScenario(Constants.Area, AboutProjectScenarios.GetAllProjectTagsInCollection, false);
                    return data && data.allProjectTagsInCollection ? data.allProjectTagsInCollection : [];
                },
                (error: Error) => {
                    this._performanceService.endScenario(Constants.Area, AboutProjectScenarios.GetAllProjectTagsInCollection, false, undefined, {
                        status: "failed"
                    });
                    throw error;
                }
            );
    }

    public saveProjectDescription(newDescription: string, projectId: string): Promise<void> {
        this._performanceService.startScenario(AboutProjectScenarios.SaveDescription, false);
        return this._pageContext
            .getRestClient<ICoreRestClient>("ICoreRestClient")
            .updateProject({ description: newDescription } as TeamProject, projectId)
            .then(
                opref => {
                    return this._pollOperationResult(opref.id);
                },
                (error: Error) => {
                    this._performanceService.endScenario(Constants.Area, AboutProjectScenarios.SaveDescription, false, undefined, {
                        status: "failed"
                    });
                    throw error;
                }
            )
            .then(
                () => {
                    this._performanceService.endScenario(Constants.Area, AboutProjectScenarios.SaveDescription, false);
                },
                (error: Error) => {
                    this._performanceService.endScenario(Constants.Area, AboutProjectScenarios.SaveDescription, false, undefined, {
                        status: "failed"
                    });
                    throw error;
                }
            );
    }

    public saveReadmeSetting(readmeSetting: string, projectId: string): Promise<void> {
        this._performanceService.startScenario(AboutProjectScenarios.SaveReadMeSettings, false);
        return this._pageContext
            .getService<ISettingsService>("ISettingsService")
            .setEntries({ "VersionControl/ProjectOverview/DefaultRepository": readmeSetting }, SettingsUserScope.Host, ProjectScopeName, projectId)
            .then(
                () => {
                    this._performanceService.endScenario(Constants.Area, AboutProjectScenarios.SaveReadMeSettings, false);
                },
                (error: Error) => {
                    this._performanceService.endScenario(Constants.Area, AboutProjectScenarios.SaveReadMeSettings, false, undefined, {
                        status: "failed"
                    });
                    throw error;
                }
            );
    }

    public saveProjectTag(addedTags: string[], removedTags: string[], projectId: string): Promise<void> {
        const createPatchDocument = (): JsonPatchDocument => {
            const operations: JsonPatchOperation[] = [];

            addedTags.forEach(function(tag: string) {
                operations.push({
                    from: "",
                    op: Operation.Add,
                    path: "/" + ProjectTagsConstants.PropertyPrefix + tag,
                    value: "true"
                } as JsonPatchOperation);
            });
            removedTags.forEach(function(tag: string) {
                operations.push({
                    from: "",
                    op: Operation.Remove,
                    path: "/" + ProjectTagsConstants.PropertyPrefix + tag,
                    value: null
                } as JsonPatchOperation);
            });
            return operations as JsonPatchDocument;
        };
        this._performanceService.startScenario(AboutProjectScenarios.SaveProjectTags, false);
        return this._pageContext
            .getRestClient<ICoreRestClient>("ICoreRestClient")
            .setProjectProperties(projectId, createPatchDocument())
            .then(
                () => {
                    this._performanceService.endScenario(Constants.Area, AboutProjectScenarios.SaveProjectTags, false);
                },
                (error: Error) => {
                    this._performanceService.endScenario(Constants.Area, AboutProjectScenarios.SaveProjectTags, false, undefined, {
                        status: "failed"
                    });
                    throw error;
                }
            );
    }

    private _pollOperationResult = async (opId: string): Promise<void> => {
        if (opId) {
            let completed = false;

            try {
                const op = await this._pageContext
                    .getRestClient<IOperationsRestClient>("OperationsForProjectOverview.IOperationsRestClient")
                    .getOperation(opId);

                switch (op.status) {
                    case OperationStatus.Succeeded:
                        completed = true;
                        return Promise.resolve();
                    case OperationStatus.Cancelled:
                    case OperationStatus.Failed:
                        completed = true;
                        return Promise.reject(op.resultMessage);
                }
            } catch {
                return Promise.reject("operation failed");
            }

            if (!completed) {
                return wait(200).then(() => this._pollOperationResult(opId));
            }
        }
    };
}
